import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _size from 'lodash/size';
import PropTypes from 'prop-types';
// Models
import mCarpark from 'models/cpm/carpark';
// Permissions
import permissions from 'constants/Permissions';
// HOCs
import withPermissions from 'hocs/WithPermissions';
import withLoading from 'hocs/WithLoading';
import withTitle from 'hocs/WithTitle';
// Custom
import { cpmGetCarparksList } from 'actions';
import { fullListShape } from 'constants/PropTypesShapes';
import MainListTemplate from 'components/templates/MainListTemplate';
import { navigateToShow, navigateToBook } from 'cpmCarparks/navigation';
import LoadingDisplayer from 'components/misc/LoadingDisplayer';
import { MaxWidthContext } from 'components/misc/Contexts';
import CarparkRow from 'cpmCarparks/assets/main/CarparkRow';
import CarparkFilters from 'cpmCarparks/assets/main/CarparkFilters';

const CarparksList = ({
  location: { query: { stay: stayQueryParam } },
  cpmCarparkList,
  dispatchGetCarparksList,
  handleStartLoading,
  hasPermission,
}) => {
  const contentMaxWidth = React.useContext(MaxWidthContext);

  const [initialLoading, setInitialLoading] = React.useState(true);
  const carparksListIsFull = cpmCarparkList.elements.data && cpmCarparkList.elements.data.length > 0;

  const isRedirecting = React.useRef(false);

  React.useEffect(() => dispatchGetCarparksList({ page: 1, pageSize: 20 }, [], [], true), []);
  /* ✅ ⬇️ */
  // Redirect if only one carpark is listed
  React.useEffect(
    function manageRedirectionIfOneCarpark() {
      if (!cpmCarparkList.loading && carparksListIsFull && initialLoading && !isRedirecting.current) {
        const only1CarparkInList = cpmCarparkList.elements.totalEntries === 1 && _size(cpmCarparkList.elements.data) === 1;

        // Redirect if only one carpark available for user and there is no explicit stay query param
        if (!stayQueryParam && only1CarparkInList) {
          const singleCarpark = cpmCarparkList.elements.data[0];

          const hasManagementPermission = hasPermission({
            needed: permissions.pCpm.management,
            permissionsOrContext: singleCarpark.permissions,
          });

          const hasCarparkBookingsPermission = hasPermission({
            needed: permissions.pBooking.bookings,
            permissionsOrContext: singleCarpark.permissions,
          });

          if (hasManagementPermission) {
            isRedirecting.current = true;
            navigateToShow(singleCarpark.id);
          }

          if (!hasManagementPermission && hasCarparkBookingsPermission) {
            isRedirecting.current = true;
            navigateToBook(singleCarpark.id);
          }
        // If there are several carparks availables and no automatic redirection, we end up the initial loading to display all carparks
        } else if (initialLoading) setInitialLoading(false);
      }
    },
    [cpmCarparkList.loading],
  );

  const handleRowDbleClick = (carpark) => {
    if (carpark) {
      handleStartLoading();
      setTimeout(() => navigateToShow(carpark.id), 500);
    }
  };

  return (
    <LoadingDisplayer
      loading={initialLoading}
      mountChildrenWhileLoading
      forceHideChildren={initialLoading}
    >
      <div
        className="w-100 h-100 d-flex flex-column align-items-center justify-content-space-between"
        style={{ gap: '32px' }}
      >
        <MainListTemplate
          title={<LocalizedMessage id="title.myCarparksList" />}
          icon={mCarpark.icon}
          contentMaxWidth={contentMaxWidth}
          displayRefresh
          withDivider={false}
          name={mCarpark.name}
          listFeatures={mCarpark.listFeatures}
          withActions
          nbActions={2}
          elementsData={{
            loading: cpmCarparkList.loading,
            elements: cpmCarparkList.elements,
            handleGetList: dispatchGetCarparksList,
          }}
          onRowDbleClick={handleRowDbleClick}
          rowComp={(props) => <CarparkRow {...props} />}
          filtersComp={<CarparkFilters />}
        />
      </div>
    </LoadingDisplayer>
  );
};

CarparksList.propTypes = {
  location: PropTypes.shape({
    query: PropTypes.shape({
      stay: PropTypes.string,
    }),
  }).isRequired,
  cpmCarparkList: fullListShape.isRequired,
  hasPermission: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
  dispatchGetCarparksList: PropTypes.func.isRequired,
  handleStartLoading: PropTypes.func.isRequired,
  mePermissions: PropTypes.arrayOf(PropTypes.string),
  slugId: PropTypes.number.isRequired,
};

CarparksList.defaultProps = {
  mePermissions: null,
};

const mapStateToProps = (state) => ({
  cpmCarparkList: state.cpmCarpark.list,
  mePermissions: state.me.permissions,
  slugId: state.cpmSlug.current.result.id,
});

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const { dispatch } = dispatchProps;

  return {
    ...ownProps,
    ...stateProps,
    dispatchGetCarparksList: (pagination, sort, filters, count = false) => {
      dispatch(
        cpmGetCarparksList(
          mCarpark.queListCarparks,
          { slugId: stateProps.slugId },
          { pagination, sort, filters },
          count,
        ),
      );
    },
  };
};

module.exports = compose(
  withPermissions,
  connect(mapStateToProps, null, mergeProps),
  withLoading,
  withTitle('pageTitle.myCarparks'),
)(CarparksList);



// WEBPACK FOOTER //
// ./routes/cpm/routes/carparks/routes/list/components/CarparksList.js